import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

import useUserlist from "../stores/users";
import TableSkeleton from "../components/skeletons/TableSkeleton";

// --- Sub-components ---

// 1. Skeleton Loader Component

export default function Users() {
  const navigate = useNavigate();

  // Store State
  const {
    users,
    admins,
    loaded,
    isSubmitting,
    isExporting,
    error,
    actions: { getUsers, getAdmins, createAdmin, exportUsers },
  } = useUserlist();

  // Local State
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [activeTab, setActiveTab] = useState("users");
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [pageSize, setPageSize] = useState(5);
  const [sortBy, setSortBy] = useState("user_id");
  const [sortOrder, setSortOrder] = useState("asc");

  // Admin Form State
  const [newAdmin, setNewAdmin] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    status: "Active",
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch Data Effect
  useEffect(() => {
    const params = {
      search: searchTerm || undefined,
      status: selectedStatus || undefined,
      page: 1,
      size: pageSize,
      sort_by: sortBy,
      order: sortOrder,
    };

    const debounceTimer = setTimeout(() => {
      if (activeTab === "users") {
        getUsers(params);
      } else {
        getAdmins(params);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [
    searchTerm,
    selectedStatus,
    pageSize,
    activeTab,
    getUsers,
    getAdmins,
    sortBy,
    sortOrder,
  ]);

  const dataToDisplay =
    (activeTab === "users" ? users.results : admins.results) || [];
  const paginationData = activeTab === "users" ? users : admins;

  // --- Handlers ---

  const handleViewDetails = (userId) => {
    navigate(`/admin-v2/users/${userId}`);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAdmin({ ...newAdmin, [name]: value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!newAdmin.name) errors.name = "Name is required";
    if (!newAdmin.email) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(newAdmin.email))
      errors.email = "Email is invalid";
    if (!newAdmin.password) errors.password = "Password is required";
    else if (newAdmin.password.length < 6)
      errors.password = "Password must be at least 6 characters";
    if (newAdmin.password !== newAdmin.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    return errors;
  };

  const handleAddAdminSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const adminPayload = {
      username: newAdmin.name,
      email: newAdmin.email,
      password: newAdmin.password,
      is_active: newAdmin.status === "Active",
    };

    const success = await createAdmin(adminPayload);

    if (success) {
      setShowAddAdminModal(false);
      setNewAdmin({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        status: "Active",
      });
      setFormErrors({});
    } else {
      alert(`Failed to create admin. ${error?.message || ""}`);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1) return;

    const params = {
      page: newPage,
      search: searchTerm || undefined,
      status: selectedStatus || undefined,
      size: pageSize,
      sort_by: sortBy,
      order: sortOrder,
    };

    if (activeTab === "users") {
      getUsers(params);
    } else {
      getAdmins(params);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm("");
    setSelectedStatus("");
    setPageSize(5);
    setSortBy("user_id"); // Optional: reset sort when changing tab
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Improved Sort Handler: Prevents sorting while loading
  const handleSort = (column) => {
    if (!loaded) return; // Block interaction if loading

    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  console.log("loaded", loaded);

  // --- Sub-Components (Defined inside to access state easily) ---

  const SortableTh = ({ label, column, isSortable = true }) => {
    const isActive = sortBy === column;

    return (
      <th
        scope="col"
        onClick={() => {
          if (!isSortable) return;
          handleSort(column);
        }}
        className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider select-none transition-colors duration-200 
          ${
            !loaded
              ? "text-gray-300 cursor-not-allowed pointer-events-none opacity-60" // Disabled styles
              : "text-gray-500 cursor-pointer hover:bg-gray-100 hover:text-gray-700" // Active styles
          }
        `}
      >
        <div className="flex items-center gap-1">
          {label}
          <span className="inline-flex w-4">
            {isActive && (
              <span className="text-blue-500 font-bold">
                {sortOrder === "asc" ? (
                  <ArrowUpIcon className="w-[12px] h-[12px]" />
                ) : (
                  <ArrowDownIcon className="w-[12px] h-[12px]" />
                )}
              </span>
            )}
          </span>
        </div>
      </th>
    );
  };

  // Calculate Column Count for Skeleton
  const totalColumns = activeTab === "users" ? 11 : 6;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 text-left">Users</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage user accounts and permissions
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {activeTab === "users" && (
            <button
              type="button"
              onClick={exportUsers}
              disabled={isExporting}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md 
    ${
      isExporting
        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
        : "bg-white text-gray-700 hover:bg-gray-50"
    } 
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              <ArrowDownTrayIcon
                className={`-ml-1 mr-2 h-5 w-5`} // Add simple animation
                aria-hidden="true"
              />
              {/* Update text to indicate polling */}
              {isExporting ? "Processing..." : "Export Data"}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowAddAdminModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Add New Admin
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => handleTabChange("users")}
            className={`${
              activeTab === "users"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <UserGroupIcon
              className={`${
                activeTab === "users" ? "text-blue-500" : "text-gray-400"
              } -ml-0.5 mr-2 h-5 w-5`}
            />
            Regular Users
          </button>
          <button
            onClick={() => handleTabChange("admins")}
            className={`${
              activeTab === "admins"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ShieldCheckIcon
              className={`${
                activeTab === "admins" ? "text-blue-500" : "text-gray-400"
              } -ml-0.5 mr-2 h-5 w-5`}
            />
            Admin Users
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 shadow rounded-lg space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
          <div className="flex-grow">
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2"
                placeholder="Search by name, email, or ID"
              />
            </div>
          </div>
          <div className="flex-shrink-0">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="flex-shrink-0">
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={15}>15 per page</option>
              <option value={20}>20 per page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableTh label="ID" column="user_id" />
                    <SortableTh label="Name" column="name" />
                    <SortableTh label="Email" column="email" />

                    {activeTab === "users" && (
                      <SortableTh label="Referral" column="referral_code" />
                    )}
                    {activeTab === "users" && (
                      <SortableTh label="Bots" column="bot_count" />
                    )}

                    <SortableTh label="Status" column="status" />

                    {activeTab === "users" && (
                      <SortableTh label="Sub Type" column="subscription" />
                    )}

                    <SortableTh label="Joined" column="join_date" />
                    <SortableTh label="Last Login" column="last_login" />
                    {activeTab === "users" && (
                      <SortableTh label="Phone" column="whatsapp_number" isSortable={false} />
                    )}
                    <SortableTh label="Deactivation Reason" column="deactivation_reason" />
                    <SortableTh label="" column="actions" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* LOGIC: Show Skeleton if not loaded, else show Data or Empty state */}
                  {!loaded ? (
                    <TableSkeleton
                      rowCount={pageSize}
                      colCount={totalColumns}
                    />
                  ) : dataToDisplay.length === 0 ? (
                    <tr>
                      <td
                        colSpan={totalColumns}
                        className="px-6 py-10 whitespace-nowrap text-center text-sm text-gray-500"
                      >
                        No users found.
                      </td>
                    </tr>
                  ) : (
                    dataToDisplay.map((user) => (
                      <tr
                        key={user.id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleViewDetails(user.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-left">
                          {user.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                          {activeTab === "admins" ? user.username : user.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                          {user.email}
                        </td>

                        {activeTab === "users" && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                            {user.referral_code ?? "-"}
                          </td>
                        )}
                        {activeTab === "users" && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                            {user.bot_count}
                          </td>
                        )}

                        <td className="px-6 py-4 whitespace-nowrap text-left">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              user.is_active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {user.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>

                        {activeTab === "users" && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                            {user.subscription_type ?? "Free"}
                          </td>
                        )}

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                          {formatDate(user.date_joined)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                          {formatDate(user.last_login)}
                        </td>
                        {activeTab === "users" && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                            {user.whatsapp_number ?? "-"}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left max-w-[160px]">
                          <div
                            className="truncate"
                            title={user.deactivation_reason ?? "-"}
                          >
                            {user.deactivation_reason ?? "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                          <Link
                            to={`/users/${user.id}`}
                            className="underline text-[#4f46e5]"
                          >
                            Details
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {loaded && paginationData.total > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          {/* Mobile Pagination */}
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(paginationData.page - 1)}
              disabled={paginationData.page <= 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(paginationData.page + 1)}
              disabled={!paginationData.has_next}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>

          {/* Desktop Pagination */}
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-medium">
                  {(paginationData.page - 1) * paginationData.size + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(
                    paginationData.page * paginationData.size,
                    paginationData.total,
                  )}
                </span>{" "}
                of <span className="font-medium">{paginationData.total}</span>{" "}
                results
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Pagination"
              >
                <button
                  onClick={() => handlePageChange(paginationData.page - 1)}
                  disabled={paginationData.page <= 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                <button
                  onClick={() => handlePageChange(paginationData.page + 1)}
                  disabled={!paginationData.has_next}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Add Admin Modal (Simplified for brevity, logic remains same) */}
      {showAddAdminModal && (
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowAddAdminModal(false)}
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3
                        className="text-lg leading-6 font-medium text-gray-900"
                        id="modal-title"
                      >
                        Add New Admin
                      </h3>
                      <button
                        onClick={() => setShowAddAdminModal(false)}
                        className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                      >
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                    <form onSubmit={handleAddAdminSubmit} className="space-y-4">
                      {/* Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={newAdmin.name}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                        {formErrors.name && (
                          <p className="mt-1 text-sm text-red-600">
                            {formErrors.name}
                          </p>
                        )}
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={newAdmin.email}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                        {formErrors.email && (
                          <p className="mt-1 text-sm text-red-600">
                            {formErrors.email}
                          </p>
                        )}
                      </div>

                      {/* Password */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Password
                        </label>
                        <input
                          type="password"
                          name="password"
                          value={newAdmin.password}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                        {formErrors.password && (
                          <p className="mt-1 text-sm text-red-600">
                            {formErrors.password}
                          </p>
                        )}
                      </div>

                      {/* Confirm Password */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Confirm Password
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={newAdmin.confirmPassword}
                          onChange={handleInputChange}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                        {formErrors.confirmPassword && (
                          <p className="mt-1 text-sm text-red-600">
                            {formErrors.confirmPassword}
                          </p>
                        )}
                      </div>

                      {/* Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Status
                        </label>
                        <select
                          name="status"
                          value={newAdmin.status}
                          onChange={handleInputChange}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </div>

                      {/* Buttons */}
                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-blue-300"
                        >
                          {isSubmitting ? "Adding..." : "Add Admin"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAddAdminModal(false)}
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:w-auto sm:text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
